import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Search, Globe } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { notificationsService } from '@/services/notificationsService'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import i18n from '@/i18n'

export default function Header({ pageTitle, pageSubtitle }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [langOpen, setLangOpen] = useState(false)

  // Poll unread notification count
  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: notificationsService.getUnreadCount,
    refetchInterval: 60_000,
    retry: false,
  })

  const unreadCount = unreadData?.unreadCount || 0

  const toggleLang = () => {
    const next = i18n.language === 'ar' ? 'en' : 'ar'
    i18n.changeLanguage(next)
    document.documentElement.dir = next === 'ar' ? 'rtl' : 'ltr'
    setLangOpen(false)
  }

  return (
    <header
      className="hg-glass flex items-center px-6 py-3 gap-4 flex-shrink-0 z-40"
      style={{ height: 64 }}
    >
      {/* Page title */}
      <div className="flex-1 min-w-0">
        <h1
          className="font-black text-navy text-xl leading-tight truncate"
          style={{ fontFamily: 'Cairo, sans-serif' }}
        >
          {pageTitle}
        </h1>
        {pageSubtitle && (
          <p className="text-ghost text-xs truncate" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            {pageSubtitle}
          </p>
        )}
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Language toggle */}
        <button
          onClick={toggleLang}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-bold transition-all hover:bg-surface-band"
          style={{ color: '#767683', fontFamily: 'Cairo, sans-serif' }}
          title="تغيير اللغة / Change Language"
        >
          <Globe size={16} />
          <span>{i18n.language === 'ar' ? 'EN' : 'عر'}</span>
        </button>

        {/* Notifications bell */}
        <button
          onClick={() => navigate(ROUTES.NOTIFICATIONS)}
          className="relative p-2 rounded-xl transition-all hover:bg-surface-band text-dim hover:text-navy"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
              style={{ background: '#FF6D00' }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* User avatar */}
        <div
          className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl cursor-pointer hover:bg-surface-band transition-all"
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{ background: '#000666' }}
          >
            {user?.name?.charAt(0) || 'A'}
          </div>
          <div className="hidden md:block">
            <div
              className="text-sm font-bold text-ink leading-none"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              {user?.name || 'Admin'}
            </div>
            <div
              className="text-[11px] mt-0.5"
              style={{ color: '#767683', fontFamily: 'Tajawal, sans-serif' }}
            >
              {t('common.active')}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
